const KEY = "rivets.access-version.v1";
const HISTORY_KEY = "rivetsAccessVersion";
type Versions = Record<string, number | "refreshing">;
const observed: Versions = {};

function read(): Versions {
  let history: Versions = {};
  try { history = window.history.state?.[HISTORY_KEY] ?? {}; } catch { /* Unavailable history. */ }
  try { return { ...(JSON.parse(sessionStorage.getItem(KEY) ?? "{}") ?? {}), ...history }; }
  catch { return history; }
}

function save(orgId: string, revision: number | "refreshing"): boolean {
  const versions = { ...read(), ...observed, [orgId]: revision };
  observed[orgId] = revision;
  try {
    sessionStorage.setItem(KEY, JSON.stringify(versions));
    // Remove a prior fallback so it cannot override a newer storage value.
    try {
      if (window.history.state?.[HISTORY_KEY]) window.history.replaceState({ ...window.history.state, [HISTORY_KEY]: undefined }, "");
    } catch {
      // A stale history fallback could mask the saved value after reload.
      // Keep the cleared screen rather than repeatedly reloading that state.
      return false;
    }
    return true;
  }
  catch {
    // History state survives reload, including when browser storage is disabled.
    try { window.history.replaceState({ ...window.history.state, [HISTORY_KEY]: versions }, ""); return true; }
    catch { return false; }
  }
}

/**
 * Records the member's access revision for an organization and says whether
 * it moved since the last one this tab saw. No stored revision is "unknown",
 * not "changed" (CONTRACT C4): the first handshake only establishes the
 * baseline, as does the one after a reload that left the organization.
 */
export function observeAccessVersion(orgId: string, revision: number): { changed: boolean } {
  const previous = observed[orgId] ?? read()[orgId];
  save(orgId, revision);
  return { changed: typeof previous === "number" && previous !== revision };
}

/** The next handshake follows a full reset, so it can establish its baseline. */
export function markAccessRefreshing(orgId: string): void { save(orgId, "refreshing"); }
